import {Box, Tooltip, Typography} from '@mui/material';
import React, {useCallback} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import ACCOUNT_TAB from '../../constants/AccountTab';
import {useReviewContext} from '../../contexts/ReviewContext';
import {useSubmitHandover} from '../../hooks/useSubmitHandover';
import {ShowIfDebugWrapper} from '../../contexts/DebugProvider';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';
import {GlobalPermissionEnum} from '../../schema-types/globalTypes';
import {SaltusAny} from '../../utils/SaltusAny';
import {themeColors} from '../../theme/Theme';
import {sideMenuWidth} from '../SideMenu/SideMenu';
import {Button} from '../Button/Button';
import {HandoverValidationMessage} from './HandoverValidationMessage';

export const HandoverFooterNavigation: React.FC<{
  handleSave?: () => SaltusAny | Promise<any>;
  onNext?: () => Promise<any>;
  onNextLoading?: boolean;
  nextIcon?: SaltusAny;
  onExit?: () => Promise<any>;
  exitingLoading?: boolean;
  disabled?: boolean;
}> = ({
  handleSave,
  onNext,
  onNextLoading,
  exitingLoading,
  onExit,
  nextIcon,
  disabled,
}) => {
  const context = useReviewContext();
  const {accountId} = useParams();
  const navigate = useNavigate();
  const {fullSubmit, submissionStatus} = useSubmitHandover();

  const goNext = useCallback(() => {
    if (context.nextStep) {
      navigate(context.getHandoverPageURL(context.nextStep));
    }
  }, [navigate, context]);

  const goBack = useCallback(() => {
    if (context.previousStep) {
      return navigate({
        pathname: `${context.getHandoverPageURL(context.previousStep)}`,
        search: `back=true`,
      });
    }
    // if the review is allowed to be edited:
    if (context.reviewValidation?.isDraft) {
      return navigate({
        pathname: `/account/${accountId}/review-journey/${context.reviewValidation.id}/header-precursor`,
      });
    }
    // otherwise, just go back
    navigate(-1);
  }, [navigate, context]);

  const goExit = useCallback(
    () => navigate({pathname: `/account/${accountId}/${ACCOUNT_TAB.REVIEWS}`}),
    [navigate]
  );

  const handleNext = useCallback(async () => {
    if (onNext) {
      await onNext()
        .catch((error) => {
          console.error('Error during onNext', error);
        })
        .then((shouldContinue) => {
          if (shouldContinue === false) {
            return;
          }
          goNext();
        });
    } else if (context.onNextFn) {
      debugger;
      await context
        .onNextFn()
        .catch((error) => {
          console.error('Error during context.onNextFn', error);
        })
        .then((shouldContinue) => {
          if (shouldContinue === false) {
            return;
          }
          goNext();
        });
    } else {
      goNext();
    }
  }, [onNext, goNext]);

  const handleSaveAndExit = useCallback(() => {
    // doesnt actually save. done on purpose as its autosaving.
    if (onExit) {
      onExit()
        .then(() => goExit())
        .catch((error) => {
          console.error('Error during onNext', error);
        });
    } else if (context.onSaveAndCloseFn) {
      debugger;
      context
        .onSaveAndCloseFn()
        .then(() => goExit())
        .catch((error) => {
          console.error('Error during context.onSaveAndCloseFn', error);
        });
    } else {
      goExit();
    }
  }, [onExit, goExit]);

  return (
    <>
      <div style={{height: '8rem', width: '100%'}} />
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: sideMenuWidth,
          display: 'flex',
          flexDirection: 'column',
          right: 0,
          paddingTop: 2,
          paddingBottom: 2,
          paddingLeft: 3,
          paddingRight: 3,
          background: themeColors.mainBackgroundColor,
          zIndex: (theme) => theme.zIndex.appBar + 1,
        }}
      >
        {context.currentStep === 'summary' && <HandoverValidationMessage />}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <div style={{flex: 1}}>
            {context.previousStep ? (
              <Button
                eventName="Previous handover step"
                sx={{marginRight: 'auto'}}
                onClick={() => goBack()}
                variant="contained"
                color="secondary"
                data-testid="back-button"
              >
                Back
              </Button>
            ) : (
              <div>
                <Button
                  eventName="Back to meeting"
                  sx={{marginRight: 'auto'}}
                  onClick={() => goBack()}
                  variant="contained"
                  color="secondary"
                  data-testid="back-button"
                >
                  Back to {context.reviewValidation?.isDraft ? 'Meeting' : 'Account'}
                </Button>
              </div>
            )}
          </div>

          {context.reviewDetails?.isDraft || context.reviewDetails?.isFailedSubmission ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                eventName="Save handover and exit"
                data-testid="handover-save-and-exit"
                sx={{marginLeft: 'auto', marginRight: 'auto'}}
                disabled={exitingLoading}
                variant="contained"
                color="primary"
                type="submit"
                size="large"
                onClick={handleSaveAndExit}
              >
                {exitingLoading ? `Saving...` : `Save and exit`}
              </Button>
            </div>
          ) : null}
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              eventName="Exit handover without save"
              sx={{marginLeft: 'auto', marginRight: 'auto'}}
              disabled={exitingLoading}
              variant="outlined"
              color="primary"
              type="submit"
              size="large"
              onClick={goExit}
              data-testid="exit-button"
            >
              Exit without saving
            </Button>
          </div>

          <div
            style={{
              flex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-evenly',
            }}
          >
            {context.fullValidationLoading ? (
              <>
                <Typography sx={{marginRight: 2}} color="coral">
                  Checking review validation...
                </Typography>
              </>
            ) : null}

            {handleSave &&
            (context.reviewValidation?.isDraft ||
              context.reviewDetails?.isFailedSubmission ||
              context.reviewEditable) ? (
              <Button
                sx={{mr: 2}}
                eventName="Submit Handover"
                data-testid="submit-button"
                disabled={
                  context.softSubmitValid === false ||
                  disabled ||
                  context.fullValidationLoading
                }
                variant="contained"
                color="primary"
                type="submit"
                size="large"
                onClick={handleSave}
              >
                {submissionStatus === 'submitting' ? 'Submitting...' : 'Submit'}
              </Button>
            ) : null}
            {context.nextStep ? (
              <Button
                eventName="Next handover step"
                data-test="Handover Next"
                data-testid="Handover Next"
                onClick={handleNext}
                variant="contained"
                color="primary"
                startIcon={nextIcon}
                type="button"
                sx={{textAlign: 'left'}}
                disabled={onNextLoading}
              >
                {onNextLoading ? 'Loading...' : 'Next'}
              </Button>
            ) : null}
            {context.currentStep === 'summary' ? (
              <FeatureBoundary
                showErrorIfDenied={false}
                features={[GlobalPermissionEnum.can_access_admin_features]}
              >
                <ShowIfDebugWrapper debugKey="forms">
                  <Box ml={2}>
                    <Tooltip title="This is used for admins to skip the soft/full submit confirmation popups and warnings">
                      <Button
                        eventName="Admin full submit handover"
                        disabled={submissionStatus === 'submitting'}
                        size="small"
                        variant="contained"
                        color="warning"
                        onClick={() => fullSubmit()}
                      >
                        {submissionStatus === 'submitting'
                          ? 'Submitting...'
                          : 'Admin full submission'}
                      </Button>
                    </Tooltip>
                  </Box>
                </ShowIfDebugWrapper>
              </FeatureBoundary>
            ) : null}
          </div>
        </Box>
      </Box>
    </>
  );
};
